import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * @typedef {Object} LayeredSlideshowRefs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/**
 * @typedef {Object} DragState
 * @property {number} target
 * @property {number} start
 * @property {number} max
 * @property {number} activeSize - The resolved pixel size of the active panel at drag start
 * @property {number} [inactiveSize] - Desktop equal-width inactive panel size in px
 * @property {boolean} left
 * @property {boolean} [dragging]
 * @property {boolean} [prevent]
 * @property {number} [progress]
 */

const DRAG_THRESHOLD = 5;
const MAX_DRAG_WIDTH_RATIO = 0.8;
const DRAG_COMPLETE_THRESHOLD = 0.5;
const INACTIVE_SIZE = 56; // Px size of inactive tabs on desktop (legacy / unused on equal-width)
const INACTIVE_MOBILE_SIZE = 288; // Px height of inactive tabs on mobile (2× previous 144)
/** Desktop: active column share vs each inactive (fr units) */
const DESKTOP_ACTIVE_RATIO = 2.5;
const DESKTOP_INACTIVE_RATIO = 1;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** @extends {Component<LayeredSlideshowRefs>} */
export class LayeredSlideshowComponent extends Component {
  requiredRefs = ['container'];
  #active = 0;
  /** @type {DragState | null} */
  #drag = null;
  /** @type {AbortController | null} */
  #abort = null;
  #isMobile = false;
  /** @type {ResizeObserver | null} */
  #heightObserver = null;
  /** @type {MutationObserver | null} */
  #contentObserver = null;
  /** @type {ResizeObserver | null} */
  #containerObserver = null;

  /** @returns {number} The inactive tab size in pixels based on current viewport */
  get #inactiveSize() {
    return this.#isMobile ? INACTIVE_MOBILE_SIZE : INACTIVE_SIZE;
  }

  connectedCallback() {
    super.connectedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );

    this.#isMobile = isMobileBreakpoint();
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);

    this.#containerObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          // Use contentBoxSize if available for better precision, or fallback to contentRect
          const boxSize = entry.contentBoxSize[0];
          const isMobile = this.#isMobile;

          let size;
          if (boxSize) {
            size = isMobile ? boxSize.blockSize : boxSize.inlineSize;
          } else {
            size = isMobile ? entry.contentRect.height : entry.contentRect.width;
          }

          this.#updateGridSizes(size);
          if (this.#isFitHeight() && !isMobile) {
            this.#syncHeight();
            this.#updateGridSizes(size);
          }
        }
      }
    });
    this.#containerObserver.observe(this.refs.container);

    this.#updateActiveTab();
    this.#setupEventListeners();
    this.#observeContentHeight();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { container, tabs, panels } = this.refs;

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', (e) => this.#handleTabClick(e, i), opts);
      tab.addEventListener('focus', (e) => this.#handleTabFocus(e, i), opts);
      tab.addEventListener('pointerenter', () => this.#handleDesktopHover(i), opts);
    }

    for (const [i, panel] of panels?.entries() ?? []) {
      panel.addEventListener('pointerenter', () => this.#handleDesktopHover(i), opts);
    }

    this.#setupPanelFocusManagement(opts);

    if (!this.#isMobile) {
      container.addEventListener('pointerdown', (e) => this.#startDrag(e), opts);
      container.addEventListener('click', (e) => this.#preventClickDuringDrag(e), { ...opts, capture: true });
    }
  }

  /**
   * Desktop: expand panel on hover (first is open on load via #active = 0).
   * @param {number} index
   */
  #handleDesktopHover(index) {
    if (this.#isMobile || this.#drag?.dragging) return;
    this.#activate(index);
  }

  #handleKeydown(/** @type {KeyboardEvent} */ e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs) return;

    const i = tabs.indexOf(target);
    const navMap = {
      [this.#isMobile ? 'ArrowUp' : 'ArrowLeft']: -1,
      [this.#isMobile ? 'ArrowDown' : 'ArrowRight']: 1,
      Home: -i,
      End: tabs.length - 1 - i,
    };

    const offset = navMap[e.key];
    if (offset !== undefined) {
      e.preventDefault();
      const nextIndex = (i + offset + tabs.length) % tabs.length;
      tabs[nextIndex]?.focus();
      this.#activate(nextIndex);
    }
  }

  #handleTabClick(/** @type {MouseEvent} */ e, /** @type {number} */ index) {
    e.preventDefault();
    this.#activate(index);
  }

  #handleTabFocus(/** @type {FocusEvent} */ e, /** @type {number} */ index) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.matches(':focus-visible')) {
      this.#activate(index);
    }
  }

  /**
   * @param {AddEventListenerOptions & { signal: AbortSignal }} opts
   */
  #setupPanelFocusManagement(opts) {
    const { panels } = this.refs;
    if (!panels) return;

    for (const [index, panel] of panels.entries()) {
      panel.addEventListener('keydown', (event) => this.#handlePanelKeydown(event, index), opts);
    }
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #handlePanelKeydown(event, index) {
    if (event.key !== 'Tab') return;

    const { panels } = this.refs;
    const panel = /** @type {HTMLElement} */ (event.currentTarget);
    const focusable = this.#getFocusableElements(panel);
    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    if (event.shiftKey) {
      const isAtStart =
        (firstFocusable && document.activeElement === firstFocusable) ||
        (!focusable.length && document.activeElement === panel);
      if (isAtStart && index > 0) {
        event.preventDefault();
        this.#activate(index - 1);
        this.#focusPanelEdge(index - 1, 'end');
      }
      return;
    }

    const isAtEnd =
      (lastFocusable && document.activeElement === lastFocusable) ||
      (!focusable.length && document.activeElement === panel);

    if (isAtEnd && panels && index < panels.length - 1) {
      event.preventDefault();
      this.#activate(index + 1);
      this.#focusPanelEdge(index + 1, 'start');
    }
  }

  /**
   * @param {number} index
   * @param {'start' | 'end'} [position]
   */
  #focusPanelEdge(index, position = 'start') {
    const panel = this.refs.panels?.[index];
    if (!panel) return;

    const focusable = this.#getFocusableElements(panel);
    const target = position === 'end' ? focusable[focusable.length - 1] : focusable[0];

    requestAnimationFrame(() => (target ?? panel).focus());
  }

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLElement[]}
   */
  #getFocusableElements(panel) {
    return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((el) => !el.closest('[inert]'))
      .map((el) => /** @type {HTMLElement} */ (el));
  }

  #preventClickDuringDrag(/** @type {MouseEvent} */ e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (this.#drag?.prevent && target.closest('[role="tab"]')) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }

  #handleMediaQueryChange = () => {
    const wasMobile = this.#isMobile;
    this.#isMobile = isMobileBreakpoint();

    if (wasMobile !== this.#isMobile) {
      const { container } = this.refs;
      container.setAttribute('data-instant-transitions', '');

      this.#clearHeightStyles();
      // Re-calculate height first so grid calculation has correct container dimensions
      this.#observeContentHeight();
      this.#updateActiveTab();
      this.#setupEventListeners();

      requestAnimationFrame(() => {
        container.removeAttribute('data-instant-transitions');
      });
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#heightObserver?.disconnect();
    this.#heightObserver = null;
    this.#contentObserver?.disconnect();
    this.#contentObserver = null;
    this.#containerObserver?.disconnect();
    this.#containerObserver = null;
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Public method to select a slide by index
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  select(index, { instant = false } = {}) {
    this.#activate(index, instant);
  }

  /**
   * @param {number} index
   * @param {boolean} [instant]
   */
  #activate(index, instant = false) {
    const { container, tabs } = this.refs;
    if (!tabs || index === this.#active || index < 0 || index >= tabs.length) return;

    if (instant) {
      container.setAttribute('data-instant-transitions', '');
    }

    this.#active = index;
    this.#updateActiveTab();

    if (instant) {
      // Double rAF to ensure layout is fully settled before re-enabling transitions
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          container.removeAttribute('data-instant-transitions');
        });
      });
    }
  }

  #updateActiveTab() {
    const { tabs, panels } = this.refs;

    for (const [i, tab] of tabs?.entries() ?? []) {
      const isActive = i === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels?.entries() ?? []) {
      const isActive = i === this.#active;
      panel.toggleAttribute('inert', !isActive);
      panel.setAttribute('tabindex', isActive ? '0' : '-1');

      const video = panel.querySelector('video');
      if (video) {
        isActive ? video.play() : video.pause();
      }
    }

    this.#updateGridSizes();
    this.#syncHeight();
    // Fit-height open width depends on settled panel height
    if (this.#isFitHeight() && !this.#isMobile) {
      this.#updateGridSizes();
    }
  }

  /** @returns {boolean} */
  #isFitHeight() {
    return this.refs.container?.getAttribute('data-image-fit') === 'height';
  }

  /**
   * @param {HTMLElement | undefined | null} panel
   * @returns {number} Shopify image aspect ratio (width / height)
   */
  #getSlideAspectRatio(panel) {
    if (!panel) return 0.75;
    return (
      parseFloat(getComputedStyle(panel).getPropertyValue('--slide-aspect-ratio')) || 0.75
    );
  }

  /** @returns {number} */
  #getContainerHeight() {
    const { container } = this.refs;
    const fromContainer = container?.getBoundingClientRect().height ?? 0;
    if (fromContainer > 1) return fromContainer;
    const fromHost = this.getBoundingClientRect().height;
    if (fromHost > 1) return fromHost;
    return parseFloat(getComputedStyle(this).minHeight) || 0;
  }

  /**
   * @param {number} [containerSize] - Override container size (used during viewport switch)
   */
  #updateGridSizes(containerSize) {
    const { container, tabs, panels } = this.refs;
    if (!tabs) return;

    if (!this.#isMobile) {
      if (this.#isFitHeight()) {
        const containerWidth = containerSize ?? container.getBoundingClientRect().width;
        const { active, inactive } = this.#getDesktopPxSizes(containerWidth, tabs.length);
        const sizes = tabs.map((_, i) => (i === this.#active ? `${active}px` : `${inactive}px`));
        container.style.setProperty('--active-tab', sizes.join(' '));
        container.style.setProperty('--expanded-panel-width', `${active}px`);
        return;
      }

      container.style.removeProperty('--expanded-panel-width');
      const sizes = tabs.map((_, i) =>
        i === this.#active ? `${DESKTOP_ACTIVE_RATIO}fr` : `${DESKTOP_INACTIVE_RATIO}fr`
      );
      container.style.setProperty('--active-tab', sizes.join(' '));
      return;
    }

    container.style.removeProperty('--expanded-panel-width');
    const inactiveSize = this.#inactiveSize;
    const activePanel = panels?.[this.#active];
    const activeSize = this.#getMobileExpandedHeight(activePanel);
    const sizes = tabs.map((_, i) => (i === this.#active ? `${activeSize}px` : `${inactiveSize}px`));
    container.style.setProperty('--active-tab', sizes.join(' '));
    container.style.setProperty('--active-panel-height', `${activeSize}px`);
    container.style.height = `${activeSize + inactiveSize * (tabs.length - 1)}px`;
  }

  /**
   * Mobile open height from slide image aspect ratio (width / ratio).
   * @param {HTMLElement | undefined} panel
   * @returns {number}
   */
  #getMobileExpandedHeight(panel) {
    const { container } = this.refs;
    const width = container.getBoundingClientRect().width;
    const styles = panel ? getComputedStyle(panel) : null;
    const ratio =
      parseFloat(styles?.getPropertyValue('--slide-aspect-ratio-mobile') || '') ||
      parseFloat(styles?.getPropertyValue('--slide-aspect-ratio') || '') ||
      0.75;
    const imageHeight = width / ratio;
    const contentHeight = this.#getPanelContentHeight(panel);
    return Math.max(imageHeight, contentHeight, 150);
  }

  /**
   * @param {HTMLElement | undefined} panel
   * @returns {number}
   */
  #getPanelContentHeight(panel) {
    if (!panel) return 0;
    const content = panel.querySelector('.layered-slideshow__content');
    if (!content) return 0;

    const inner = /** @type {HTMLElement} */ (content.querySelector('.group-block-content') ?? content);
    const savedHeight = inner.style.height;
    inner.style.height = 'auto';

    const styles = getComputedStyle(content);
    const paddingTop = parseFloat(styles.paddingBlockStart || styles.paddingTop) || 0;
    const paddingBottom = parseFloat(styles.paddingBlockEnd || styles.paddingBottom) || 0;
    const height = (inner.scrollHeight || 0) + paddingTop + paddingBottom;

    inner.style.height = savedHeight;
    return height;
  }

  /**
   * Pixel sizes for desktop layout / drag.
   * With image_fit=height, active width stops at the image's natural width at panel height.
   * @param {number} containerWidth
   * @param {number} count
   */
  #getDesktopPxSizes(containerWidth, count) {
    const totalRatio = DESKTOP_ACTIVE_RATIO + DESKTOP_INACTIVE_RATIO * (count - 1);
    const defaultActive = (DESKTOP_ACTIVE_RATIO / totalRatio) * containerWidth;
    const defaultInactive = (DESKTOP_INACTIVE_RATIO / totalRatio) * containerWidth;

    if (!this.#isFitHeight()) {
      return { active: defaultActive, inactive: defaultInactive };
    }

    const activePanel = this.refs.panels?.[this.#active];
    const height = this.#getContainerHeight();
    const ratio = this.#getSlideAspectRatio(activePanel);
    const imageWidth = height > 0 ? height * ratio : defaultActive;
    const minInactive = Math.max(48, Math.min(defaultInactive, containerWidth * 0.08));
    const maxActive = Math.max(minInactive, containerWidth - minInactive * Math.max(0, count - 1));
    // Open only up to the full image width — never past it
    const active = Math.min(imageWidth, maxActive);
    const inactive = count > 1 ? (containerWidth - active) / (count - 1) : 0;

    return { active, inactive };
  }

  /**
   * @param {PointerEvent} event
   */
  #startDrag(event) {
    if (this.#isMobile) return;

    const { tabs } = this.refs;
    if (!tabs) return;

    const eventTarget = /** @type {HTMLElement} */ (event.target);
    const tab = eventTarget.closest('[role="tab"]');
    if (tab) {
      const i = tabs.indexOf(/** @type {HTMLElement} */ (tab));
      if (i === this.#active) return;
      const target = i > this.#active ? i + 1 : i;
      if (target >= tabs.length) return;

      this.#initializeDrag(event, target);
    } else {
      this.#initializeDrag(event);
    }
  }

  /**
   * @param {PointerEvent} event
   * @param {number} [initialTarget]
   */
  #initializeDrag(event, initialTarget) {
    const { container, tabs } = this.refs;
    if (!tabs) return;

    const containerWidth = container.getBoundingClientRect().width;
    const { active: activeSize, inactive: inactiveSize } = this.#getDesktopPxSizes(containerWidth, tabs.length);

    this.#drag = {
      target: initialTarget ?? -1,
      start: event.clientX,
      max: containerWidth * MAX_DRAG_WIDTH_RATIO,
      activeSize,
      inactiveSize,
      left: initialTarget !== undefined ? initialTarget > this.#active : false,
    };

    const ac = new AbortController();
    const opts = { signal: ac.signal };

    document.addEventListener('pointermove', (e) => this.#handleDrag(e), opts);
    document.addEventListener('pointerup', () => this.#endDrag(ac), opts);
    document.addEventListener('pointercancel', () => this.#endDrag(ac), opts);

    event.preventDefault();
  }

  /**
   * @param {PointerEvent} event
   */
  #handleDrag(event) {
    if (!this.#drag) return;

    const { container, tabs } = this.refs;
    if (!container || !tabs) return;

    const delta = event.clientX - this.#drag.start;
    const move = Math.abs(delta);

    if (!this.#drag.dragging && move >= DRAG_THRESHOLD) {
      if (this.#drag.target === -1) {
        if (delta > 0 && this.#active > 0) {
          this.#drag.target = this.#active - 1;
          this.#drag.left = false;
        } else if (delta < 0 && this.#active < tabs.length - 1) {
          this.#drag.target = this.#active + 1;
          this.#drag.left = true;
        } else {
          return;
        }
      }
      this.#drag.dragging = true;
      container.setAttribute('data-dragging', '');
    }

    if (!this.#drag.dragging) return;

    const correct = this.#drag.left ? delta < 0 : delta > 0;
    const progress = correct ? Math.min(move / this.#drag.max, 1) : 0;

    const inactiveSize = this.#drag.inactiveSize ?? this.#inactiveSize;
    const activeSize = this.#drag.activeSize;
    const range = activeSize - inactiveSize;
    let currentActiveWidth = activeSize;
    const sizes = tabs.map((_, i) => {
      if (i === this.#active) {
        const active = Math.max(inactiveSize, activeSize - range * progress);
        currentActiveWidth = active;
        return `${active}px`;
      }
      if (i === this.#drag?.target) {
        const drag = inactiveSize + range * progress;
        return `${drag}px`;
      }
      return `${inactiveSize}px`;
    });

    container.style.setProperty('--active-tab', sizes.join(' '));
    if (this.#isFitHeight()) {
      container.style.setProperty('--expanded-panel-width', `${currentActiveWidth}px`);
    }
    this.#drag.progress = progress;
  }

  /**
   * @param {AbortController} ac
   */
  #endDrag(ac) {
    if (!this.#drag) return;

    const { container } = this.refs;
    container?.removeAttribute('data-dragging');

    if (this.#drag.dragging) {
      this.#drag.prevent = true;
      setTimeout(() => (this.#drag = null), 100);

      if (this.#drag.progress && this.#drag.progress >= DRAG_COMPLETE_THRESHOLD) {
        this.#activate(this.#drag.target);
      } else {
        this.#updateActiveTab();
      }
    } else {
      this.#drag = null;
    }

    ac.abort();
  }

  #observeContentHeight() {
    const { panels } = this.refs;

    this.#heightObserver?.disconnect();
    this.#heightObserver = new ResizeObserver(() => {
      this.#syncHeight();
      if (this.#isFitHeight() && !this.#isMobile) this.#updateGridSizes();
    });

    this.#contentObserver?.disconnect();
    this.#contentObserver = new MutationObserver(() => {
      this.#syncHeight();
      if (this.#isFitHeight() && !this.#isMobile) this.#updateGridSizes();
    });

    for (const panel of panels ?? []) {
      const content = panel.querySelector('.layered-slideshow__content');
      const inner = content?.querySelector('.group-block-content');

      // Observe all relevant elements for resize
      if (inner) this.#heightObserver.observe(inner);
      if (content) this.#heightObserver.observe(content);

      // Observe content for DOM mutations (new blocks, text changes)
      const target = inner ?? content;
      if (target) {
        this.#contentObserver.observe(target, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    }

    this.#syncHeight();
  }

  #syncHeight() {
    const { container } = this.refs;
    const contentHeight = this.#getMaxContentHeight();
    const size = container.getAttribute('size');
    const isAuto = size === 'auto';
    const isAdaptImage = size === 'adapt_image';
    const isFullScreen = size === 'full-screen';

    // Mobile always uses image aspect height, regardless of desktop height setting
    if (this.#isMobile) {
      this.#syncMobileHeight(contentHeight);
      return;
    }

    if (isFullScreen) {
      this.#syncFullScreenHeight(contentHeight);
      return;
    }

    if (isAdaptImage) {
      this.#syncDesktopAdaptHeight(contentHeight);
    } else {
      this.#syncDesktopHeight(contentHeight, isAuto);
    }
  }

  /**
   * Desktop height from active panel image aspect at expanded panel width.
   * @param {number} contentHeight
   */
  #syncDesktopAdaptHeight(contentHeight) {
    const { container, panels } = this.refs;
    if (!container || !panels?.length) return;

    const activePanel = panels[this.#active];
    const containerWidth = container.getBoundingClientRect().width;
    const { active } = this.#getDesktopPxSizes(containerWidth, panels.length);
    const ratio =
      parseFloat(activePanel ? getComputedStyle(activePanel).getPropertyValue('--slide-aspect-ratio') : '') ||
      0.75;
    // Match media box (--expanded-panel-width ≈ active) so cover does not crop
    const imageHeight = active / ratio;
    const height = Math.max(imageHeight, contentHeight, 150);

    container.style.height = `${height}px`;
    this.style.minHeight = `${height}px`;
    container.style.removeProperty('--active-panel-height');
  }

  /**
   * Full-screen: CSS owns viewport height on desktop.
   * @param {number} contentHeight
   */
  #syncFullScreenHeight(contentHeight) {
    const { container } = this.refs;
    if (!container) return;

    this.style.minHeight = '';
    container.style.height = '';
    container.style.removeProperty('--active-panel-height');

    const cssMinHeight = parseFloat(getComputedStyle(this).minHeight) || 0;
    if (contentHeight > cssMinHeight) {
      this.style.minHeight = `${contentHeight}px`;
      container.style.height = `${contentHeight}px`;
    }
  }

  /**
   * @param {number} contentHeight
   * @param {boolean} isAuto
   */
  #syncDesktopHeight(contentHeight, isAuto) {
    const { container } = this.refs;

    if (isAuto) {
      // Auto mode: fit to content height
      let minHeightTemp = Math.max(contentHeight, 150);
      container.style.height = `${minHeightTemp}px`;
      this.style.minHeight = `${minHeightTemp}px`;
    } else {
      // Temporarily clear inline style to measure CSS-defined min-height
      const savedMinHeight = this.style.minHeight;
      this.style.minHeight = '';
      const cssMinHeight = parseFloat(getComputedStyle(this).minHeight) || 0;
      this.style.minHeight = savedMinHeight;

      // Only set inline heights when content exceeds CSS min-height
      if (contentHeight > cssMinHeight) {
        this.style.minHeight = `${contentHeight}px`;
        container.style.height = `${contentHeight}px`;
      } else {
        this.style.minHeight = '';
        container.style.height = '';
      }
    }
  }

  /**
   * Mobile: open panel height always follows image aspect ratio.
   * @param {number} [_contentHeight]
   */
  #syncMobileHeight(_contentHeight) {
    this.style.minHeight = '';
    this.#updateGridSizes();
  }

  #clearHeightStyles() {
    const { container } = this.refs;

    this.style.minHeight = '';
    container.style.height = '';
    container.style.removeProperty('--active-panel-height');
  }

  #getMaxContentHeight() {
    const { panels } = this.refs;
    let max = 0;

    for (const panel of panels ?? []) {
      const content = panel.querySelector('.layered-slideshow__content');
      if (!content) continue;

      const inner = /** @type {HTMLElement} */ (content.querySelector('.group-block-content') ?? content);

      // Temporarily set height to auto for accurate measurement
      // This is needed because height: 100% collapses when parent has no height
      const savedHeight = inner.style.height;
      inner.style.height = 'auto';

      const styles = getComputedStyle(content);
      const paddingTop = parseFloat(styles.paddingBlockStart || styles.paddingTop) || 0;
      const paddingBottom = parseFloat(styles.paddingBlockEnd || styles.paddingBottom) || 0;

      const height = (inner.scrollHeight || 0) + paddingTop + paddingBottom;
      if (height > max) max = height;

      // Restore original height
      inner.style.height = savedHeight;
    }

    return max;
  }
}

customElements.define('layered-slideshow-component', LayeredSlideshowComponent);
